import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { extractErrorMessage } from '@/lib/api-client';
import { useVehiclesList } from '@/features/vehicles/api';
import {
  SUPPLIER_TYPE_LABELS,
  useSuppliersList,
} from '@/features/suppliers/api';
import { useCreateMaintenanceOrder } from './api';
import {
  ITEM_TYPES,
  PRIORITY_LABELS,
  TYPE_LABELS,
  type MaintenanceType,
  type OrderPriority,
} from './types';


const ORDER_TYPES: MaintenanceType[] = ['preventive', 'corrective', 'recall'];
const PRIORITIES: OrderPriority[] = ['low', 'normal', 'high', 'urgent'];

interface ItemDraft {
  key: string;
  description: string;
  type: string;
  quantity: string;
  unitPriceCents: string; // em centavos como string
  odometer: string; // km (ou horas) da troca -- so aparece em pneu/borracharia
  performedAt: string; // data do servico -- so aparece em lavagem
}

function newItem(): ItemDraft {
  return {
    key: Math.random().toString(36).slice(2, 10),
    description: '',
    type: 'service',
    quantity: '1',
    unitPriceCents: '0',
    odometer: '',
    performedAt: '',
  };
}

function parseBRLToCents(value: string): number {
  const normalized = value.replace(/\./g, '').replace(',', '.').trim();
  const n = Number.parseFloat(normalized || '0');
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(n * 100);
}

function formatCentsBRL(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

function todayLocalISO(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface MaintenanceOrderFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MaintenanceOrderFormDialog({
  open,
  onOpenChange,
}: MaintenanceOrderFormDialogProps) {
  const [vehicleId, setVehicleId] = useState('');
  const [supplierId, setSupplierId] = useState<string>('none');
  const [type, setType] = useState<MaintenanceType>('preventive');
  const [priority, setPriority] = useState<OrderPriority>('normal');
  const [description, setDescription] = useState('');
  const [scheduledAt, setScheduledAt] = useState<string>(todayLocalISO());
  // Vazio de proposito: a OS costuma nascer agendada, e a execucao so e
  // conhecida depois. Quem registra servico ja feito preenche na hora.
  const [completedAt, setCompletedAt] = useState<string>('');
  const [odometer, setOdometer] = useState<string>('');
  const [items, setItems] = useState<ItemDraft[]>([newItem()]);

  const vehiclesQuery = useVehiclesList({ limit: 100, active: 'true', sort: 'plate' });
  // Precisa no escopo do componente: o rotulo do hodometro muda por item (pneu).
  const selectedVehicle = (vehiclesQuery.data?.data ?? []).find((v) => v.id === vehicleId);
  const isHourmeter = Boolean(
    selectedVehicle?.tracksHourmeter && !selectedVehicle?.tracksOdometer,
  );
  const suppliersQuery = useSuppliersList({ limit: 100, active: 'true' });
  const create = useCreateMaintenanceOrder();

  useEffect(() => {
    if (open) {
      setVehicleId('');
      setSupplierId('none');
      setType('preventive');
      setPriority('normal');
      setDescription('');
      setScheduledAt(todayLocalISO());
      setOdometer('');
      setItems([newItem()]);
    }
  }, [open]);

  const updateItem = (key: string, patch: Partial<ItemDraft>) => {
    setItems((arr) => arr.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  };
  const removeItem = (key: string) => {
    setItems((arr) => (arr.length === 1 ? arr : arr.filter((it) => it.key !== key)));
  };
  const addItem = () => setItems((arr) => [...arr, newItem()]);

  const validItems = items.filter((i) => i.description.trim().length > 0);
  const totalCents = useMemo(
    () =>
      validItems.reduce((sum, it) => {
        const qty = Number.parseFloat(it.quantity.replace(',', '.') || '0') || 0;
        const unitCents = parseBRLToCents(it.unitPriceCents);
        return sum + Math.round(qty * unitCents);
      }, 0),
    [validItems],
  );

  const canSubmit =
    vehicleId.length > 0 && validItems.length > 0 && !create.isPending;

  const handleSubmit = async () => {
    try {
      const payload = {
        vehicleId,
        supplierId: supplierId === 'none' ? undefined : supplierId,
        type,
        priority,
        description: description.trim() || undefined,
        scheduledAt: scheduledAt ? new Date(scheduledAt).toISOString() : undefined,
        completedAt: completedAt ? new Date(completedAt).toISOString() : undefined,
        odometerMeters: odometer
          ? Math.round(Number.parseFloat(odometer.replace(',', '.')) * 1000)
          : undefined,
        items: validItems.map((it) => {
          const qty = Number.parseFloat(it.quantity.replace(',', '.') || '1') || 1;
          const unitCents = parseBRLToCents(it.unitPriceCents);
          return {
            description: it.description.trim(),
            type: it.type,
            // Mesma conversao do hodometro da OS: o valor digitado e km (ou horas).
            odometerMeters: it.odometer
              ? Math.round(Number.parseFloat(it.odometer.replace(',', '.')) * 1000)
              : undefined,
            performedAt: it.performedAt ? new Date(it.performedAt).toISOString() : undefined,
            quantityCents: Math.round(qty * 100),
            unitPriceCents: unitCents,
            totalCents: Math.round(qty * unitCents),
          };
        }),
      };
      await create.mutateAsync(payload);
      toast.success('Ordem de serviço criada');
      onOpenChange(false);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Nova ordem de serviço</DialogTitle>
          <DialogDescription>
            A OS começa como rascunho. Você pode aprovar e iniciar depois de criada.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="vehicle">Veículo *</Label>
              <Select value={vehicleId} onValueChange={setVehicleId}>
                <SelectTrigger id="vehicle">
                  <SelectValue
                    placeholder={
                      vehiclesQuery.isLoading
                        ? 'Carregando…'
                        : (vehiclesQuery.data?.data ?? []).length === 0
                          ? 'Nenhum veículo ativo'
                          : 'Selecione um veículo'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {(vehiclesQuery.data?.data ?? []).map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.plate}
                      {(v.brand || v.model) && ` — ${[v.brand, v.model].filter(Boolean).join(' ')}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="scheduled">Data agendada</Label>
              <Input
                id="scheduled"
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="executed">Data de execução</Label>
              <Input
                id="executed"
                type="datetime-local"
                value={completedAt}
                onChange={(e) => setCompletedAt(e.target.value)}
              />
              <p className="text-[11px] text-muted-foreground">
                Quando o serviço foi feito. Deixe em branco se ainda não foi.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="type">Tipo *</Label>
              <Select value={type} onValueChange={(v) => setType(v as MaintenanceType)}>
                <SelectTrigger id="type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ORDER_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {TYPE_LABELS[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="priority">Prioridade *</Label>
              <Select value={priority} onValueChange={(v) => setPriority(v as OrderPriority)}>
                <SelectTrigger id="priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => (
                    <SelectItem key={p} value={p}>
                      {PRIORITY_LABELS[p]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="supplier">Fornecedor</Label>
              <Select value={supplierId} onValueChange={setSupplierId}>
                <SelectTrigger id="supplier">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    Sem fornecedor (manutenção interna)
                  </SelectItem>
                  {(suppliersQuery.data?.data ?? []).map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                      <span className="ml-2 text-xs text-muted-foreground">
                        · {SUPPLIER_TYPE_LABELS[s.type]}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(suppliersQuery.data?.data ?? []).length === 0 &&
                !suppliersQuery.isLoading && (
                  <p className="text-[11px] text-muted-foreground">
                    Nenhum fornecedor cadastrado. Cadastre em "Fornecedores" no menu lateral.
                  </p>
                )}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="description">Descrição</Label>
            <Input
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ex.: Revisão dos 50.000 km"
            />
          </div>

          {(() => {
            return (
              <div className="space-y-1.5">
                <Label htmlFor="odometer">
                  {isHourmeter ? 'Horímetro (horas)' : 'Hodômetro (km)'}
                </Label>
                <Input
                  id="odometer"
                  inputMode="decimal"
                  value={odometer}
                  onChange={(e) => setOdometer(e.target.value)}
                  placeholder={isHourmeter ? 'Ex.: 2350' : 'Ex.: 235800'}
                />
              </div>
            );
          })()}

          {/* Itens */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Itens da OS *</Label>
              <Button type="button" variant="outline" size="sm" onClick={addItem}>
                <Plus className="size-4" />
                Adicionar item
              </Button>
            </div>

            <div className="space-y-3">
              {items.map((item, idx) => (
                <div
                  key={item.key}
                  className="space-y-3 rounded-lg border border-border bg-card p-3"
                >
                  <div className="flex items-start gap-2">
                    <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-muted text-xs font-semibold text-muted-foreground">
                      {idx + 1}
                    </div>
                    <Input
                      className="flex-1"
                      value={item.description}
                      onChange={(e) => updateItem(item.key, { description: e.target.value })}
                      placeholder="Descrição (ex.: Troca de óleo)"
                      maxLength={255}
                    />
                    {items.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-8 text-destructive"
                        onClick={() => removeItem(item.key)}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    )}
                  </div>

                  <div className="grid gap-2 sm:grid-cols-4">
                    <div>
                      <Label className="text-[11px] uppercase text-muted-foreground">Tipo</Label>
                      <Select
                        value={item.type}
                        onValueChange={(v) => updateItem(item.key, { type: v })}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ITEM_TYPES.map((t) => (
                            <SelectItem key={t.value} value={t.value}>
                              {t.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase text-muted-foreground">
                        Quantidade
                      </Label>
                      <Input
                        className="h-8"
                        inputMode="decimal"
                        value={item.quantity}
                        onChange={(e) => updateItem(item.key, { quantity: e.target.value })}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-[11px] uppercase text-muted-foreground">
                        Preço unit. (R$)
                      </Label>
                      <Input
                        className="h-8 font-mono"
                        inputMode="decimal"
                        value={item.unitPriceCents}
                        onChange={(e) => updateItem(item.key, { unitPriceCents: e.target.value })}
                        placeholder="0,00"
                      />
                    </div>
                  </div>

                  {/* Pedidos do Mario (09/2026): o km em que o pneu foi trocado e a
                      data da lavagem. Aparecem so no tipo a que pertencem, para nao
                      encher a linha de campo vazio. */}
                  {(item.type === 'tire' || item.type === 'tire_shop') && (
                    <div className="grid gap-2 sm:grid-cols-4">
                      <div className="sm:col-span-2">
                        <Label className="text-[11px] uppercase text-muted-foreground">
                          {isHourmeter ? 'Horímetro da troca' : 'Km da troca'}
                        </Label>
                        <Input
                          className="h-8"
                          inputMode="decimal"
                          value={item.odometer}
                          onChange={(e) => updateItem(item.key, { odometer: e.target.value })}
                          placeholder={isHourmeter ? 'Ex.: 2350' : 'Ex.: 235800'}
                        />
                      </div>
                    </div>
                  )}

                  {item.type === 'wash' && (
                    <div className="grid gap-2 sm:grid-cols-4">
                      <div className="sm:col-span-2">
                        <Label className="text-[11px] uppercase text-muted-foreground">
                          Data da lavagem
                        </Label>
                        <Input
                          className="h-8"
                          type="date"
                          value={item.performedAt}
                          onChange={(e) => updateItem(item.key, { performedAt: e.target.value })}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {validItems.length === 0 && (
              <p className="text-xs text-destructive">
                Pelo menos um item com descrição é obrigatório.
              </p>
            )}

            <div className="flex items-center justify-end gap-3 border-t border-border pt-3 text-sm">
              <span className="text-muted-foreground">Total estimado:</span>
              <span className="font-mono text-base font-semibold">
                {formatCentsBRL(totalCents)}
              </span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {create.isPending ? 'Criando…' : 'Criar OS'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
